import React, { useState, useEffect } from 'react';
import { authService } from '../services/api';

function FinalOrders() {
  const [finalOrders, setFinalOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filter states
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [searchProduct, setSearchProduct] = useState('');
  const [groupBy, setGroupBy] = useState('none'); // none, week, month, product
  
  const user = authService.getCurrentUser();

  useEffect(() => {
    loadFinalOrders();
  }, []);

  const loadFinalOrders = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://b1.envicblue.co.uk/api/weekly_orders.php?action=approved', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ward_stock_token')}`
        }
      });
      const data = await response.json();
      
      if (data.success) {
        setFinalOrders(Array.isArray(data.data) ? data.data : []);
      } else {
        setFinalOrders([]);
        setError(data.message || 'Failed to load orders');
      }
    } catch (err) {
      setFinalOrders([]);
      setError('Failed to load final orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Quick filters
  const applyQuickFilter = (filter) => {
    const today = new Date();
    let from, to;

    switch(filter) {
      case 'today':
        from = to = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        from = weekStart.toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'this_month':
        from = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        from = lastMonth.toISOString().split('T')[0];
        to = new Date(today.getFullYear(), today.getMonth(), 0).toISOString().split('T')[0];
        break;
      case 'last_7_days':
        from = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      case 'last_30_days':
        from = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        to = today.toISOString().split('T')[0];
        break;
      default:
        from = to = '';
    }

    setDateFrom(from);
    setDateTo(to);
    setSelectedWeek('');
    setSelectedMonth('');
  };

  const clearFilters = () => {
    setFilterStatus('all');
    setDateFrom('');
    setDateTo('');
    setSelectedWeek('');
    setSelectedMonth('');
    setSelectedYear(new Date().getFullYear().toString());
    setSearchProduct('');
    setGroupBy('none');
  };

  // Filter orders
  const filteredOrders = finalOrders.filter(order => {
    // Status filter
    if (filterStatus !== 'all' && order.status !== filterStatus) return false;

    // Date range filter
    if (dateFrom && dateTo) {
      const orderDate = new Date(order.approved_at || order.created_at);
      const from = new Date(dateFrom);
      const to = new Date(dateTo);
      to.setHours(23, 59, 59);
      if (orderDate < from || orderDate > to) return false;
    }

    // Week filter
    if (selectedWeek && order.week_number.toString() !== selectedWeek) return false;

    // Month filter
    if (selectedMonth) {
      const orderDate = new Date(order.approved_at || order.created_at);
      const orderMonth = (orderDate.getMonth() + 1).toString();
      if (orderMonth !== selectedMonth) return false;
    }

    // Year filter
    if (selectedYear && order.year.toString() !== selectedYear) return false;

    // Product search
    if (searchProduct) {
      const search = searchProduct.toLowerCase();
      const productMatch = order.product_name?.toLowerCase().includes(search) || 
                          order.product_code?.toLowerCase().includes(search);
      if (!productMatch) return false;
    }

    return true;
  });

  // Group orders
  const groupedOrders = () => {
    if (groupBy === 'none') return { 'All Orders': filteredOrders };

    const groups = {};
    
    filteredOrders.forEach(order => {
      let key;
      
      switch(groupBy) {
        case 'week':
          key = `Week ${order.week_number}, ${order.year}`;
          break;
        case 'month':
          const date = new Date(order.approved_at || order.created_at);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          key = `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
          break;
        case 'product':
          key = order.product_name;
          break;
        case 'status':
          key = order.status.charAt(0).toUpperCase() + order.status.slice(1);
          break;
        default:
          key = 'All Orders';
      }
      
      if (!groups[key]) groups[key] = [];
      groups[key].push(order);
    });

    return groups;
  };

  const exportToCSV = () => {
    const csvContent = [
      ['Order ID', 'Product Code', 'Product Name', 'Quantity', 'Status', 'Week', 'Year', 'Approved Date', 'Notes'],
      ...filteredOrders.map(order => [
        order.weekly_order_id,
        order.product_code,
        order.product_name,
        order.total_quantity,
        order.status,
        order.week_number,
        order.year,
        order.approved_at ? new Date(order.approved_at).toLocaleDateString() : 'N/A',
        order.notes || ''
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `final-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    const totalQty = filteredOrders.reduce((sum, o) => sum + parseInt(o.total_quantity || 0), 0);
    
    const filterInfo = [];
    if (dateFrom && dateTo) filterInfo.push(`Date: ${dateFrom} to ${dateTo}`);
    if (selectedWeek) filterInfo.push(`Week: ${selectedWeek}`);
    if (selectedMonth) filterInfo.push(`Month: ${selectedMonth}`);
    if (selectedYear) filterInfo.push(`Year: ${selectedYear}`);
    if (filterStatus !== 'all') filterInfo.push(`Status: ${filterStatus}`);
    if (searchProduct) filterInfo.push(`Product: ${searchProduct}`);
    
    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Final Orders Report</title>
        <style>
          @media print {
            @page { margin: 15mm; }
            body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, sans-serif;
            color: #333;
            line-height: 1.6;
            padding: 20px;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 3px solid #667eea;
            padding-bottom: 20px;
          }
          .header h1 {
            font-size: 28px;
            color: #667eea;
            margin-bottom: 10px;
          }
          .header .subtitle {
            font-size: 14px;
            color: #666;
          }
          .info-box {
            background: #f9f9f9;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            gap: 10px;
          }
          .info-item {
            flex: 1;
            min-width: 150px;
          }
          .info-label {
            font-size: 12px;
            color: #666;
            margin-bottom: 5px;
          }
          .info-value {
            font-size: 18px;
            font-weight: bold;
            color: #667eea;
          }
          .filters {
            background: #fff3cd;
            padding: 10px 15px;
            border-radius: 6px;
            margin-bottom: 20px;
            border-left: 4px solid #ffc107;
          }
          .filters-title {
            font-weight: bold;
            margin-bottom: 5px;
            color: #856404;
          }
          .filters-content {
            font-size: 13px;
            color: #856404;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 12px;
            text-align: left;
            font-size: 13px;
            font-weight: 600;
          }
          td {
            padding: 10px 12px;
            border-bottom: 1px solid #e0e0e0;
            font-size: 13px;
          }
          tr:hover {
            background: #f9f9f9;
          }
          .status {
            display: inline-block;
            padding: 4px 10px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }
          .status-approved { background: #d1fae5; color: #065f46; }
          .status-rejected { background: #fee2e2; color: #991b1b; }
          .status-completed { background: #ddd6fe; color: #5b21b6; }
          .footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e0e0e0;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
          .print-date {
            font-style: italic;
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>🏥 Ward Stock System</h1>
          <div class="subtitle">Final Orders Report - B1 Cardiology Department</div>
        </div>

        <div class="info-box">
          <div class="info-item">
            <div class="info-label">Total Orders</div>
            <div class="info-value">${filteredOrders.length}</div>
          </div>
          <div class="info-item">
            <div class="info-label">Total Quantity</div>
            <div class="info-value">${totalQty} units</div>
          </div>
          <div class="info-item">
            <div class="info-label">Report Date</div>
            <div class="info-value" style="font-size: 14px;">${new Date().toLocaleDateString('en-GB')}</div>
          </div>
        </div>

        ${filterInfo.length > 0 ? `
        <div class="filters">
          <div class="filters-title">🔍 Active Filters:</div>
          <div class="filters-content">${filterInfo.join(' • ')}</div>
        </div>
        ` : ''}

        <table>
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Product</th>
              <th>Code</th>
              <th>Quantity</th>
              <th>Week/Year</th>
              <th>Status</th>
              <th>Approved Date</th>
            </tr>
          </thead>
          <tbody>
            ${filteredOrders.map(order => `
              <tr>
                <td>#${order.weekly_order_id}</td>
                <td><strong>${order.product_name}</strong></td>
                <td>${order.product_code}</td>
                <td><strong>${order.total_quantity}</strong> units</td>
                <td>Week ${order.week_number}, ${order.year}</td>
                <td>
                  <span class="status status-${order.status}">
                    ${order.status.toUpperCase()}
                  </span>
                </td>
                <td>${order.approved_at ? new Date(order.approved_at).toLocaleDateString('en-GB') : 'N/A'}</td>
              </tr>
              ${order.notes ? `
              <tr>
                <td colspan="7" style="background: #f9f9f9; font-size: 12px; font-style: italic; color: #666;">
                  💬 Notes: ${order.notes}
                </td>
              </tr>
              ` : ''}
            `).join('')}
          </tbody>
        </table>

        <div class="footer">
          <div class="print-date">
            Generated on ${new Date().toLocaleString('en-GB')} by ${user?.full_name || 'System Administrator'}
          </div>
          <div style="margin-top: 10px; color: #999;">
            Ward Stock Management System © ${new Date().getFullYear()}
          </div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;
    
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return '#10b981';
      case 'rejected': return '#ef4444';
      case 'completed': return '#667eea';
      default: return '#666';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return '✅';
      case 'rejected': return '❌';
      case 'completed': return '📦';
      default: return '📋';
    }
  };

  // Get unique values for filters
  const uniqueWeeks = [...new Set(finalOrders.map(o => o.week_number))].sort((a, b) => a - b);
  const uniqueYears = [...new Set(finalOrders.map(o => o.year))].sort((a, b) => b - a);

  const orderGroups = groupedOrders();
  const totalOrders = filteredOrders.length;
  const totalQuantity = filteredOrders.reduce((sum, o) => sum + parseInt(o.total_quantity || 0), 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px' }}>⏳</div>
          <div style={{ color: '#666', marginTop: '10px' }}>Loading orders...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
            📋 Final Orders
          </h1>
          <p style={{ color: '#666' }}>View and export approved orders</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={exportToPDF}
            style={{
              padding: '12px 24px',
              background: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📄 Export PDF
          </button>
          <button
            onClick={exportToCSV}
            style={{
              padding: '12px 24px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            📥 Export CSV
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#fee',
          border: '1px solid #fcc',
          color: '#c33',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Quick Filters */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '20px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#333', marginBottom: '15px' }}>
          ⚡ Quick Filters
        </h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
          {['today', 'this_week', 'this_month', 'last_month', 'last_7_days', 'last_30_days'].map(filter => (
            <button
              key={filter}
              onClick={() => applyQuickFilter(filter)}
              style={{
                padding: '8px 16px',
                background: '#f0f0f0',
                color: '#333',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.background = '#667eea'}
              onMouseOver={(e) => e.target.style.color = 'white'}
              onMouseLeave={(e) => {
                e.target.style.background = '#f0f0f0';
                e.target.style.color = '#333';
              }}
            >
              {filter.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced Filters */}
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '25px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#333' }}>
            🔍 Advanced Filters
          </h3>
          <button
            onClick={clearFilters}
            style={{
              padding: '6px 12px',
              background: '#f0f0f0',
              color: '#666',
              border: 'none',
              borderRadius: '6px',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            Clear All
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
          {/* Date Range */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📅 From Date
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📅 To Date
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Week Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📊 Week Number
            </label>
            <select
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">All Weeks</option>
              {uniqueWeeks.map(week => (
                <option key={week} value={week}>Week {week}</option>
              ))}
            </select>
          </div>

          {/* Month Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📆 Month
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">All Months</option>
              {['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'].map((month, idx) => (
                <option key={idx + 1} value={idx + 1}>{month}</option>
              ))}
            </select>
          </div>

          {/* Year Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📅 Year
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="">All Years</option>
              {uniqueYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📊 Status
            </label>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="all">All Status</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Product Search */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              🔍 Search Product
            </label>
            <input
              type="text"
              value={searchProduct}
              onChange={(e) => setSearchProduct(e.target.value)}
              placeholder="Product name or code..."
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Group By */}
          <div>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '600', color: '#333' }}>
              📁 Group By
            </label>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            >
              <option value="none">No Grouping</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="product">Product</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '20px',
          borderRadius: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Total Orders</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{totalOrders}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          padding: '20px',
          borderRadius: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Total Quantity</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>{totalQuantity}</div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          padding: '20px',
          borderRadius: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '14px', opacity: 0.9, marginBottom: '5px' }}>Active Filters</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold' }}>
            {[dateFrom, dateTo, selectedWeek, selectedMonth, searchProduct, filterStatus !== 'all'].filter(Boolean).length}
          </div>
        </div>
      </div>

      {/* Orders Display */}
      {filteredOrders.length === 0 ? (
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '60px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>📦</div>
          <h3 style={{ fontSize: '20px', color: '#333', marginBottom: '10px' }}>No Orders Found</h3>
          <p style={{ color: '#666' }}>Try adjusting your filters</p>
        </div>
      ) : (
        Object.entries(orderGroups).map(([groupName, orders]) => (
          <div key={groupName} style={{ marginBottom: '30px' }}>
            {groupBy !== 'none' && (
              <div style={{
                background: '#f9f9f9',
                padding: '15px 20px',
                borderRadius: '12px',
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333' }}>
                  {groupName}
                </h3>
                <span style={{ fontSize: '14px', color: '#666' }}>
                  {orders.length} orders • {orders.reduce((sum, o) => sum + parseInt(o.total_quantity || 0), 0)} items
                </span>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {orders.map((order) => (
                <div key={order.weekly_order_id} style={{
                  background: 'white',
                  borderRadius: '16px',
                  padding: '20px',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.12)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.08)';
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'start',
                    marginBottom: '15px',
                    paddingBottom: '15px',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>
                        Order #{order.weekly_order_id} • Week {order.week_number}, {order.year}
                      </div>
                      <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                        {order.product_name}
                      </h3>
                      <div style={{ fontSize: '14px', color: '#666' }}>
                        {order.product_code}
                      </div>
                    </div>
                    <span style={{
                      padding: '8px 16px',
                      background: `${getStatusColor(order.status)}20`,
                      color: getStatusColor(order.status),
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px'
                    }}>
                      {getStatusIcon(order.status)} {order.status?.toUpperCase()}
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '15px'
                  }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Quantity</div>
                      <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#667eea' }}>
                        {order.total_quantity} units
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Individual Orders</div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        {order.order_count} orders
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Approved Date</div>
                      <div style={{ fontSize: '14px', color: '#333' }}>
                        📅 {order.approved_at ? new Date(order.approved_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        }) : 'N/A'}
                      </div>
                    </div>

                    {order.notes && (
                      <div style={{ gridColumn: '1 / -1' }}>
                        <div style={{ fontSize: '12px', color: '#999', marginBottom: '5px' }}>Notes</div>
                        <div style={{
                          fontSize: '14px',
                          color: '#666',
                          padding: '10px',
                          background: '#f9f9f9',
                          borderRadius: '8px',
                          fontStyle: 'italic'
                        }}>
                          💬 {order.notes}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

export default FinalOrders;
